import { getBracketTemplate, populateTemplate } from './bracketTemplates.js';

// Build losers bracket using template
function buildTemplateLosersBracket(winnersRounds, startMatchNum, template) {
    const losersMatches = [];
    let matchNum = startMatchNum;
    let loserRoundNum = 1;

    // Dynamically generate Losers R1 from Winners R1 matches
    // LR1 has half as many matches as WR1 slots (pairs of WR1 matches feed into one LR1 match)
    // Each WR1 match contributes either "Loser of M#" (if real match) or "BYE" (if BYE match)
    if (winnersRounds.length > 0 && winnersRounds[0].length > 0) {
        const winnersR1 = winnersRounds[0];

        // Build LR1 matches by pairing consecutive WR1 matches
        // Number of LR1 matches = WR1 matches / 2
        for (let i = 0; i < winnersR1.length; i += 2) {
            const wr1Match1 = winnersR1[i];
            const wr1Match2 = winnersR1[i + 1];

            // Determine p1 from first WR1 match
            let p1;
            if (wr1Match1 && wr1Match1.p1.name !== "BYE" && wr1Match1.p2.name !== "BYE") {
                // Real match - loser drops down
                p1 = { name: `Loser of M${wr1Match1.matchNum}`, ref: `Loser of M${wr1Match1.matchNum}` };
            } else {
                // BYE match (or null) - BYE in losers
                p1 = { name: "BYE", ref: null };
            }

            // Determine p2 from second WR1 match
            let p2;
            if (wr1Match2 && wr1Match2.p1.name !== "BYE" && wr1Match2.p2.name !== "BYE") {
                // Real match - loser drops down
                p2 = { name: `Loser of M${wr1Match2.matchNum}`, ref: `Loser of M${wr1Match2.matchNum}` };
            } else {
                // BYE match (or null) - BYE in losers
                p2 = { name: "BYE", ref: null };
            }

            const match = {
                id: `l-${loserRoundNum}-${Math.floor(i / 2)}`,
                matchNum: matchNum++,
                p1,
                p2,
                from: [],
                bracket: "L",
                round: loserRoundNum,
                col: loserRoundNum,
                row: Math.floor(i / 2),
                score1: "0",
                score2: "0",
                table: "",
                started: false,
                finished: false,
                status: "Pending",
                winner: "",
            };

            losersMatches.push(match);
        }

        // Get activePlayers from the dynamically generated LR1 matches
        let activePlayers = losersMatches.map(match => ({
            name: `Winner of M${match.matchNum}`,
            ref: `Winner of M${match.matchNum}`,
            sourceMatch: match
        }));

        // Continue with remaining losers rounds using original algorithm
        // Get actual matches from winners rounds (filter out nulls) 
        const validWinnersRounds = winnersRounds.map(round => round.filter(m => m !== null));

        if (validWinnersRounds.length === 0) {
            return { losersMatches: [], nextMatchNum: matchNum, lastLoserMatch: null };
        }

        loserRoundNum++;

        // Process each subsequent winners round
        for (let wRound = 1; wRound < validWinnersRounds.length; wRound++) {
            const winnersThisRound = validWinnersRounds[wRound];
            const newLosers = winnersThisRound.map(match => ({
                name: `Loser of M${match.matchNum}`,
                ref: `Loser of M${match.matchNum}`,
                sourceMatch: match
            }));

            // Determine ordering of new losers dropping from winners bracket
            // LR2: Full reverse (to balance bracket progression)
            // LR4, LR6, etc: Alternate pairs (swap within each pair)
            let orderedNewLosers;
            if (loserRoundNum === 2) {
                // Full reverse for LR2
                orderedNewLosers = [...newLosers].reverse();
            } else if (loserRoundNum % 2 === 0 && loserRoundNum > 2) {
                // For LR4, LR6, LR8, etc: swap within pairs
                // [M25, M26, M27, M28] becomes [M26, M25, M28, M27]
                orderedNewLosers = [];
                for (let i = 0; i < newLosers.length; i += 2) {
                    if (i + 1 < newLosers.length) {
                        orderedNewLosers.push(newLosers[i + 1]); // Second of pair first
                        orderedNewLosers.push(newLosers[i]);     // First of pair second
                    } else {
                        orderedNewLosers.push(newLosers[i]);     // Odd one out
                    }
                }
            } else {
                // No reordering for odd losers rounds (LR3, LR5, etc)
                orderedNewLosers = newLosers;
            }
            const newActivePlayers = [];

            for (let i = 0; i < orderedNewLosers.length; i++) {
                const newLoser = orderedNewLosers[i];
                const activePlayer = activePlayers[i] || { name: "BYE", ref: null };

                const from = [];
                // Only connect from losers bracket matches to avoid cross-bracket lines
                if (newLoser.sourceMatch && newLoser.sourceMatch.bracket === "L") {
                    from.push(newLoser.sourceMatch.id);
                }
                if (activePlayer.sourceMatch && activePlayer.sourceMatch.bracket === "L") {
                    from.push(activePlayer.sourceMatch.id);
                }

                const match = {
                    id: `l-${loserRoundNum}-${i}`,
                    matchNum: matchNum++,
                    p1: activePlayer, // Winner from previous losers match (top position)
                    p2: newLoser, // Loser from winners bracket (bottom position)
                    from,
                    bracket: "L",
                    round: loserRoundNum,
                    col: loserRoundNum,
                    row: i,
                    score1: "0",
                    score2: "0",
                    table: "",
                    finished: false,
                    status: "Pending",
                    winner: "",
                };

                losersMatches.push(match);
                newActivePlayers.push({
                    name: `Winner of M${match.matchNum}`,
                    ref: `Winner of M${match.matchNum}`,
                    sourceMatch: match
                });
            }

            activePlayers = newActivePlayers;
            loserRoundNum++;

            // Create progression round ONLY if we have more than 1 active player 
            // AND this is not the last winners round
            if (activePlayers.length > 1 && wRound < validWinnersRounds.length - 1) {
                const progressionPlayers = [];
                for (let i = 0; i < activePlayers.length; i += 2) {
                    if (i + 1 < activePlayers.length) {
                        const from = [];
                        // Only connect from losers bracket matches to avoid cross-bracket lines
                        if (activePlayers[i].sourceMatch && activePlayers[i].sourceMatch.bracket === "L") {
                            from.push(activePlayers[i].sourceMatch.id);
                        }
                        if (activePlayers[i + 1].sourceMatch && activePlayers[i + 1].sourceMatch.bracket === "L") {
                            from.push(activePlayers[i + 1].sourceMatch.id);
                        }

                        const match = {
                            id: `l-${loserRoundNum}-${Math.floor(i / 2)}`,
                            matchNum: matchNum++,
                            p1: activePlayers[i],
                            p2: activePlayers[i + 1],
                            from,
                            bracket: "L",
                            round: loserRoundNum,
                            col: loserRoundNum,
                            row: Math.floor(i / 2),
                            score1: "0",
                            score2: "0",
                            table: "",
                            finished: false,
                            status: "Pending",
                            winner: "",
                        };

                        losersMatches.push(match);
                        progressionPlayers.push({
                            name: `Winner of M${match.matchNum}`,
                            ref: `Winner of M${match.matchNum}`,
                            sourceMatch: match
                        });
                    }
                }
                activePlayers = progressionPlayers;
                loserRoundNum++;
            }
        }

        const lastLoserMatch = losersMatches[losersMatches.length - 1] || null;

        return {
            losersMatches,
            nextMatchNum: matchNum,
            lastLoserMatch
        };
    }

    // For tournaments with no losers bracket template, return empty
    return { losersMatches: [], nextMatchNum: matchNum, lastLoserMatch: null };
}

// Build bracket using predefined template
function buildBracketFromTemplate(players) {
    const template = getBracketTemplate(players.length);
    if (!template) return null;
    
    const seeded = populateTemplate(template, players);
    const rounds = Math.ceil(Math.log2(template.slots));
    const matches = [];
    let matchNum = 1;

    // Build Winners Bracket using template
    const winnersRounds = [];
    let prevRound = [];

    // Round 1 of winners bracket
    const firstRound = [];
    for (let i = 0; i < template.slots / 2; i++) {
        const p1 = { name: seeded[i * 2], ref: null };
        const p2 = { name: seeded[i * 2 + 1], ref: null };

        // Skip completely bye matches
        if (p1.name === "BYE" && p2.name === "BYE") {
            firstRound.push(null);
            continue;
        }

        const match = {
            id: `w-1-${i}`,
            matchNum: matchNum++,
            p1,
            p2,
            from: [],
            bracket: "W",
            round: 1,
            col: 1,
            row: i,
            score1: "0",
            score2: "0",
            table: "",
            finished: false,
            status: "Pending",
            winner: "",
        };

        firstRound.push(match);
        matches.push(match);
    }
    winnersRounds.push(firstRound);
    prevRound = firstRound;

    // Subsequent winners rounds
    for (let r = 2; r <= rounds; r++) {
        const thisRound = [];
        const count = template.slots / (2 ** r);

        for (let i = 0; i < count; i++) {
            const m1 = prevRound[i * 2];
            const m2 = prevRound[i * 2 + 1];

            const p1 = m1 ? { name: `Winner of M${m1.matchNum}`, ref: `Winner of M${m1.matchNum}` } : { name: "BYE", ref: null };
            const p2 = m2 ? { name: `Winner of M${m2.matchNum}`, ref: `Winner of M${m2.matchNum}` } : { name: "BYE", ref: null };

            const from = [];
            if (m1) from.push(m1.id);
            if (m2) from.push(m2.id);

            if (p1.name === "BYE" && p2.name === "BYE") {
                thisRound.push(null);
                continue;
            }

            const match = {
                id: `w-${r}-${i}`,
                matchNum: matchNum++,
                p1,
                p2,
                from,
                bracket: "W",
                round: r,
                col: r,
                row: i,
                score1: "0",
                score2: "0",
                table: "",
                started: false,
                finished: false,
                status: "Pending",
                winner: "",
            };

            thisRound.push(match);
            matches.push(match);
        }
        winnersRounds.push(thisRound);
        prevRound = thisRound;
    }

    // Build Losers Bracket using template
    const {
        losersMatches,
        nextMatchNum,
        lastLoserMatch
    } = buildTemplateLosersBracket(winnersRounds, matchNum, template);

    matchNum = nextMatchNum;
    matches.push(...losersMatches);

    // Grand Final
    const winnersBracketFinal = winnersRounds[winnersRounds.length - 1].filter(m => m !== null)[0];
    const grandFinal = {
        id: "gf-1",
        matchNum: matchNum++,
        p1: winnersBracketFinal ? { name: `Winner of M${winnersBracketFinal.matchNum}`, ref: `Winner of M${winnersBracketFinal.matchNum}` } : { name: "BYE", ref: null },
        p2: lastLoserMatch ? { name: `Winner of M${lastLoserMatch.matchNum}`, ref: `Winner of M${lastLoserMatch.matchNum}` } : { name: "BYE", ref: null },
        from: [], // No visual connectors for Grand Final to avoid cross-bracket lines
        bracket: "GF",
        round: rounds + 1,
        col: rounds + 1,
        row: 0,
        score1: "0",
        score2: "0",
        table: "",
        finished: false,
        status: "Pending",
        winner: "",
    };
    matches.push(grandFinal);

    // Grand Final Reset (if needed)
    const grandReset = {
        id: "gf-1-reset",
        matchNum: matchNum++,
        bracket: "GF",
        round: rounds + 2,
        col: rounds + 2,
        row: 0,
        p1: { name: `Winner of M${grandFinal.matchNum}`, ref: `Winner of M${grandFinal.matchNum}` },
        p2: { name: `Loser of M${grandFinal.matchNum}`, ref: `Loser of M${grandFinal.matchNum}` },
        score1: "0",
        score2: "0",
        table: "",
        from: [grandFinal.id],
        winner: "",
        finished: false,
        status: "Pending",
    };
    matches.push(grandReset);

    return matches;
}

export function buildBracket(players) {
    // All player counts from 3-64 are now supported by templates
    return buildBracketFromTemplate(players);
}

export function getStandings(bracket) {
    // Find Grand Final matches
    const gf = bracket.find(m => m.bracket === "GF" && !m.id.includes("reset"));
    const gfReset = bracket.find(m => m.id === "gf-1-reset");
    const gfMatchNum = gf?.matchNum || 0;

    // Extract all player names from the bracket
    const allPlayers = new Set();
    bracket.forEach(match => {
        if (match.p1?.name &&
            match.p1.name !== "BYE" &&
            !match.p1.name.startsWith("Winner of") &&
            !match.p1.name.startsWith("Loser of")) {
            allPlayers.add(match.p1.name);
        }
        if (match.p2?.name &&
            match.p2.name !== "BYE" &&
            !match.p2.name.startsWith("Winner of") &&
            !match.p2.name.startsWith("Loser of")) {
            allPlayers.add(match.p2.name);
        }
    });

    // Calculate losses for each player by scanning all finished matches
    // Track: total losses and the match number where they got their 2nd loss (elimination)
    const playerStats = new Map(); // player name -> { losses: number, eliminationMatchNum: number | null }

    // Initialize all players with 0 losses
    allPlayers.forEach(player => {
        playerStats.set(player, { losses: 0, eliminationMatchNum: null });
    });

    // Sort matches by match number to process in order
    const sortedMatches = [...bracket]
        .filter(m => m.finished && m.winner)
        .sort((a, b) => (a.matchNum || 0) - (b.matchNum || 0));

    // Count losses for each player
    sortedMatches.forEach(match => {
        const loser = match.p1.name === match.winner ? match.p2.name : match.p1.name;

        if (loser && !loser.includes("Winner of") && !loser.includes("Loser of") && loser !== "BYE") {
            const stats = playerStats.get(loser);
            if (stats) {
                stats.losses++;
                // Track the match number where they got their 2nd loss
                if (stats.losses === 2 && stats.eliminationMatchNum === null) {
                    stats.eliminationMatchNum = match.matchNum;
                }
            }
        }
    });

    // Build placements array - index 0 = 1st place, index 1 = 2nd place, etc.
    const placements = [];

    // Determine 1st and 2nd place from Grand Finals
    if (gfReset && gfReset.finished && gfReset.winner && !gfReset.winner.includes("Winner of")) {
        // GF Reset happened - winner of reset is 1st, loser is 2nd
        placements[0] = gfReset.winner;
        const gfResetLoser = gfReset.p1.name === gfReset.winner ? gfReset.p2.name : gfReset.p1.name;
        if (gfResetLoser && !gfResetLoser.includes("Winner of")) {
            placements[1] = gfResetLoser;
        }
    } else if (gf && gf.finished && gf.winner && !gf.winner.includes("Winner of")) {
        // Check if winner came from winners bracket (0 losses) or losers bracket (1 loss)
        const winnerStats = playerStats.get(gf.winner);
        const gfLoser = gf.p1.name === gf.winner ? gf.p2.name : gf.p1.name;

        // If player is NOT in playerStats or has 0 losses, they came from winners bracket undefeated
        const winnerLosses = winnerStats ? winnerStats.losses : 0;

        if (winnerLosses === 0) {
            // Winner had 0 losses - they win, no reset needed
            placements[0] = gf.winner;
            if (gfLoser && !gfLoser.includes("Winner of")) {
                placements[1] = gfLoser;
            }
        }
        // If winner had 1 loss, GF Reset is needed - don't assign 1st/2nd yet
    }

    // Calculate 3rd place and beyond based on elimination match number
    // Placement = (GF match number - elimination match number) + 2
    // e.g., eliminated at M61 when GF is M62: placement = (62-61)+2 = 3rd
    // e.g., eliminated at M60 when GF is M62: placement = (62-60)+2 = 4th
    const eliminatedPlayers = [];
    playerStats.forEach((stats, playerName) => {
        if (stats.losses >= 2 && stats.eliminationMatchNum !== null) {
            // Skip players already placed in 1st or 2nd
            if (playerName === placements[0] || playerName === placements[1]) return;

            const placement = (gfMatchNum - stats.eliminationMatchNum) + 2;
            eliminatedPlayers.push({
                name: playerName,
                placement: placement,
                eliminationMatchNum: stats.eliminationMatchNum
            });
        }
    });

    // Place eliminated players in their positions
    eliminatedPlayers.forEach(player => {
        // Only set if position is valid and not already filled
        if (player.placement >= 3 && !placements[player.placement - 1]) {
            placements[player.placement - 1] = player.name;
        }
    });

    // Convert to the expected format: array of arrays (for ties)
    // Each index represents a placement (0 = 1st, 1 = 2nd, etc.)
    const places = [];
    const placed = new Set();

    for (let i = 0; i < placements.length; i++) {
        if (placements[i] && !placed.has(placements[i])) {
            places.push([placements[i]]);
            placed.add(placements[i]);
        }
    }

    // Add any remaining active players (not yet eliminated)
    const activePlayers = Array.from(allPlayers).filter(name => !placed.has(name));
    if (activePlayers.length > 0) {
        places.push(activePlayers);
    }

    return places;
}

// Individual BYE match finalization - operates on a single match only
export function finalizeSingleByeMatch(bracket, targetMatch, optionalCallback) {
    // Find the match in the bracket
    const match = bracket.find(m => m.id === targetMatch.id);
    if (!match) {
        console.warn('finalizeSingleByeMatch: Match not found in bracket');
        return false;
    }

    // Check if this match should be auto-advanced
    const shouldAdvance = shouldAutoAdvanceBye(match);
    if (!shouldAdvance) {
        return false; // Match cannot be auto-advanced
    }

    // Process the advancement for this specific match only
    processBYEAdvancement(match, shouldAdvance, optionalCallback);

    // Mark as manually finalized to prevent auto-processing in global operations
    match.manuallyFinalized = true;

    // Propagate only the direct impacts of this single match
    propagateSingleMatchWinner(bracket, match);

    return true;
}

// Undo a single BYE match without affecting others
export function undoSingleByeMatch(bracket, targetMatch) {
    // Find the match in the bracket
    const match = bracket.find(m => m.id === targetMatch.id);
    if (!match) {
        console.warn('undoSingleByeMatch: Match not found in bracket');
        return false;
    }

    const undoneMatchNum = match.matchNum;
    const loser = match.p1.name === match.winner ? match.p2.name : match.p1.name;

    // Reset the specific match
    match.finished = false;
    match.winner = '';
    match.status = 'Pending';
    match.score1 = '0';
    match.score2 = '0';
    match.manuallyFinalized = false;

    // Only reset matches that directly reference this undone match
    bracket.forEach(m => {
        if (m.id === match.id) return; // Skip the match itself

        let needsUpdate = false;

        // Check if player 1 needs to be reset (only if it came from the undone match)
        if (m.p1?.name === match.winner && m.p1?.ref && m.p1.ref.includes(`M${undoneMatchNum}`)) {
            m.p1 = { name: m.p1.ref, ref: m.p1.ref };
            needsUpdate = true;
        }
        if (m.p1?.name === loser && m.p1?.ref && m.p1.ref.includes(`M${undoneMatchNum}`)) {
            m.p1 = { name: m.p1.ref, ref: m.p1.ref };
            needsUpdate = true;
        }

        // Check if player 2 needs to be reset (only if it came from the undone match)
        if (m.p2?.name === match.winner && m.p2?.ref && m.p2.ref.includes(`M${undoneMatchNum}`)) {
            m.p2 = { name: m.p2.ref, ref: m.p2.ref };
            needsUpdate = true;
        }
        if (m.p2?.name === loser && m.p2?.ref && m.p2.ref.includes(`M${undoneMatchNum}`)) {
            m.p2 = { name: m.p2.ref, ref: m.p2.ref };
            needsUpdate = true;
        }

        // Reset any matches that became invalid due to the undo
        if (needsUpdate && m.finished) {
            m.finished = false;
            m.winner = '';
            m.status = 'Pending';
            m.score1 = '0';
            m.score2 = '0';
            m.manuallyFinalized = false;
        }
    });

    return true;
}

// Propagate the winner of a single match without affecting unrelated matches
function propagateSingleMatchWinner(bracket, match) {
    if (!match.finished) return;

    const winner = match.winner;
    const loser = match.p1.name === winner ? match.p2.name : match.p1.name;

    // Update only direct references to this specific match
    bracket.forEach(m => {
        if (m.p1?.ref === `Winner of M${match.matchNum}` || m.p1?.name === `Winner of M${match.matchNum}`) {
            m.p1.name = winner;
        }
        if (m.p2?.ref === `Winner of M${match.matchNum}` || m.p2?.name === `Winner of M${match.matchNum}`) {
            m.p2.name = winner;
        }
        if (m.p1?.ref === `Loser of M${match.matchNum}` || m.p1?.name === `Loser of M${match.matchNum}`) {
            m.p1.name = loser;
        }
        if (m.p2?.ref === `Loser of M${match.matchNum}` || m.p2?.name === `Loser of M${match.matchNum}`) {
            m.p2.name = loser;
        }
    });
}

export function finalizeByeMatches(bracket, optionalCallback) {
    // Recursively auto-advance BYE matches until no more can be advanced
    // Skip matches that were manually finalized to preserve individual operations
    let advancedAny;
    let iterationCount = 0;
    const maxIterations = 20; // Safety limit to prevent infinite loops

    do {
        advancedAny = false;
        iterationCount++;

        // First, propagate any winners from completed matches
        propagateAutoWinners(bracket);

        // Check if propagation created new BYE vs BYE scenarios from completed matches
        bracket.forEach(match => {
            if (match.finished && match.status.includes("Auto-advanced") &&
                match.p1.name === "BYE" && match.p2.name === "BYE" &&
                match.winner !== "BYE" && !match.manuallyFinalized) {
                // This match was completed as bye_vs_reference but propagation made it BYE vs BYE
                // Reset it to be processed correctly as BYE vs BYE
                match.finished = false;
                match.winner = "";
                match.status = "Pending";
            }
        });

        // Then check for new BYE matches to auto-advance in round order
        // Process winners bracket first, then losers bracket, alternating by round
        const winnerMatches = bracket.filter(m => m.bracket === "W" || m.bracket === "SE" || m.bracket === "MSE").sort((a, b) => a.round - b.round);
        const loserMatches = bracket.filter(m => m.bracket === "L").sort((a, b) => a.round - b.round);
        const grandFinalMatches = bracket.filter(m => m.bracket === "GF");

        // Get maximum rounds to alternate properly
        const maxWinnerRound = winnerMatches.length > 0 ? Math.max(...winnerMatches.map(m => m.round)) : 0;
        const maxLoserRound = loserMatches.length > 0 ? Math.max(...loserMatches.map(m => m.round)) : 0;
        const maxRound = Math.max(maxWinnerRound, maxLoserRound);

        // Process in round order: W1 → L1 → W2 → L2 → etc.
        for (let round = 1; round <= maxRound; round++) {
            // Process winners bracket matches for this round
            const winnerRoundMatches = winnerMatches.filter(m => m.round === round);
            for (const match of winnerRoundMatches) {
                if (!match.finished && !match.manuallyFinalized) {
                    const shouldAdvance = shouldAutoAdvanceBye(match);
                    if (shouldAdvance) {
                        advancedAny = true;
                        processBYEAdvancement(match, shouldAdvance, optionalCallback);
                    }
                }
            }

            // Process losers bracket matches for this round
            const loserRoundMatches = loserMatches.filter(m => m.round === round);
            for (const match of loserRoundMatches) {
                if (!match.finished && !match.manuallyFinalized) {
                    const shouldAdvance = shouldAutoAdvanceBye(match);
                    if (shouldAdvance) {
                        advancedAny = true;
                        processBYEAdvancement(match, shouldAdvance, optionalCallback);
                    }
                }
            }
        }

        // Finally process Grand Finals
        for (const match of grandFinalMatches) {
            if (!match.finished && !match.manuallyFinalized) {
                const shouldAdvance = shouldAutoAdvanceBye(match);
                if (shouldAdvance) {
                    advancedAny = true;
                    processBYEAdvancement(match, shouldAdvance, optionalCallback);
                }
            }
        }

        // Safety check to prevent infinite loops
        if (iterationCount >= maxIterations) {
            console.warn(`finalizeByeMatches: Reached maximum iterations (${maxIterations}), stopping to prevent infinite loop`);
            break;
        }

    } while (advancedAny);

    // Final propagation to ensure all winners are updated
    propagateAutoWinners(bracket);

    // After final propagation, check if any matches became BYE vs Player
    // This handles the case where propagation resolves a reference to create a new BYE match
    let finalPassAdvanced = false;
    bracket.forEach(match => {
        if (!match.finished && !match.manuallyFinalized) {
            const shouldAdvance = shouldAutoAdvanceBye(match);
            if (shouldAdvance) {
                finalPassAdvanced = true;
                processBYEAdvancement(match, shouldAdvance, optionalCallback);
            }
        }
    });

    // If we advanced any in the final pass, propagate again
    if (finalPassAdvanced) {
        propagateAutoWinners(bracket);
    }
}

// Helper function to process BYE advancement
function processBYEAdvancement(match, shouldAdvance, optionalCallback) {
    if (shouldAdvance.type === "bye_vs_bye") {
        // BYE vs BYE: winner is BYE
        match.winner = "BYE";
        match.finished = true;
        match.status = "Auto-advanced (BYE vs BYE)";
    } else if (shouldAdvance.type === "player_vs_bye") {
        // Named player vs BYE: player wins
        match.winner = shouldAdvance.winner;
        match.finished = true;
        match.status = "Auto-advanced (BYE)";
    }

    if (optionalCallback) {
        optionalCallback(match);
    }
}

// Helper function to determine if a match should auto-advance due to BYE
function shouldAutoAdvanceBye(match) {
    const p1Name = match.p1?.name || "";
    const p2Name = match.p2?.name || "";

    // Case 1: BYE vs BYE - auto-advance with BYE as winner
    if (p1Name === "BYE" && p2Name === "BYE") {
        return { type: "bye_vs_bye" };
    }

    // Case 2: Must have exactly one BYE and one other player
    const hasBye = p1Name === "BYE" || p2Name === "BYE";
    if (!hasBye) return false;

    // Get the non-BYE player name
    const nonByeName = p1Name === "BYE" ? p2Name : p1Name;

    // Case 3: BYE vs Winner/Loser of MX - DON'T auto-advance, wait for propagation
    if (nonByeName.startsWith("Winner of") || nonByeName.startsWith("Loser of")) {
        return false; // Don't advance - let propagation handle this properly
    }

    // Don't auto-advance if non-BYE name is empty or just whitespace
    if (!nonByeName || nonByeName.trim() === "") {
        return false;
    }

    // Case 4: Named player vs BYE - auto-advance the named player
    return { type: "player_vs_bye", winner: nonByeName };
}

export function propagateAutoWinners(bracket) {
    // Sort matches by match number to ensure proper propagation order
    const sortedMatches = [...bracket].sort((a, b) => (a.matchNum || 0) - (b.matchNum || 0));

    // Find Grand Final for special handling
    const grandFinal = bracket.find(m => m.id === 'gf-1');

    for (const match of sortedMatches) {
        if (match.finished) {
            const winner = match.winner;
            const loser = match.p1.name === winner ? match.p2.name : match.p1.name;

            // Special handling for Grand Final -> Grand Final Reset propagation
            // In true double elimination:
            // - p1 of Grand Final = winners bracket player (undefeated)
            // - p2 of Grand Final = losers bracket player (1 loss)
            // - If p1 (winners bracket) wins Grand Final, tournament is OVER - no reset
            // - If p2 (losers bracket) wins Grand Final, reset is needed
            const isGrandFinal = match.id === 'gf-1';
            const winnersPlayerWonGrandFinal = isGrandFinal && grandFinal &&
                grandFinal.p1?.name === winner &&
                !winner.includes('Winner of') &&
                !winner.includes('Loser of');

            // Update all references to this match's winner and loser
            for (const m of bracket) {
                // Skip Grand Final Reset if winners bracket player won the Grand Final
                const isGrandFinalResetMatch = m.id === 'gf-1-reset';
                if (isGrandFinal && isGrandFinalResetMatch && winnersPlayerWonGrandFinal) {
                    // Don't propagate to Grand Final Reset - tournament is over
                    continue;
                }

                // Check both ref property and name field for references
                if (m.p1?.ref === `Winner of M${match.matchNum}` || m.p1?.name === `Winner of M${match.matchNum}`) {
                    m.p1.name = winner;
                }
                if (m.p2?.ref === `Winner of M${match.matchNum}` || m.p2?.name === `Winner of M${match.matchNum}`) {
                    m.p2.name = winner;
                }
                if (m.p1?.ref === `Loser of M${match.matchNum}` || m.p1?.name === `Loser of M${match.matchNum}`) {
                    m.p1.name = loser;
                }
                if (m.p2?.ref === `Loser of M${match.matchNum}` || m.p2?.name === `Loser of M${match.matchNum}`) {
                    m.p2.name = loser;
                }
            }
        }
    }
}

/**
 * Resolves a placeholder reference (e.g., "Winner of M16") through hidden BYE matches
 * to find the original meaningful source reference.
 *
 * For example, if M16 is "Loser of M2" vs "BYE", then "Winner of M16" should resolve
 * to "Loser of M2" since M16 is a BYE match that would be hidden.
 *
 * @param {string} placeholderName - The placeholder text like "Winner of M16" or "Loser of M5"
 * @param {Array} bracket - The full bracket array
 * @returns {string} - The resolved placeholder text, or the original if no resolution needed
 */
export function resolvePlaceholderThroughByes(placeholderName, bracket) {
    if (!placeholderName || !bracket) return placeholderName;

    // Only process "Winner of M#" or "Loser of M#" references
    const winnerMatch = placeholderName.match(/^Winner of M(\d+)$/);
    const loserMatch = placeholderName.match(/^Loser of M(\d+)$/);

    if (!winnerMatch && !loserMatch) return placeholderName;

    const matchNum = parseInt(winnerMatch ? winnerMatch[1] : loserMatch[1], 10);
    const isWinnerRef = !!winnerMatch;

    // Find the referenced match
    const referencedMatch = bracket.find(m => m.matchNum === matchNum);
    if (!referencedMatch) return placeholderName;

    // Check if this is a BYE match (has at least one BYE player)
    const p1IsBye = referencedMatch.p1?.name === "BYE";
    const p2IsBye = referencedMatch.p2?.name === "BYE";

    // Not a BYE match - return original
    if (!p1IsBye && !p2IsBye) return placeholderName;

    // BYE vs BYE - return "BYE"
    if (p1IsBye && p2IsBye) return "BYE";

    // One player is BYE, one is not
    // Get the non-BYE player's name
    const nonByePlayer = p1IsBye ? referencedMatch.p2 : referencedMatch.p1;
    const nonByeName = nonByePlayer?.name || "";

    if (isWinnerRef) {
        // "Winner of M#" where M# is BYE match - the non-BYE player will be the winner
        // If the non-BYE player is also a reference, recurse
        if (nonByeName.startsWith("Winner of") || nonByeName.startsWith("Loser of")) {
            return resolvePlaceholderThroughByes(nonByeName, bracket);
        }
        return nonByeName || placeholderName;
    } else {
        // "Loser of M#" where M# is BYE match - BYE is the loser
        return "BYE";
    }
}